/**
 * Phase-1 packaging — maps nav items and route paths to the ModuleKey that gates
 * them, so the FE can hide nav + block routes for a customer that isn't entitled.
 *
 * Only CLEAR, unambiguous mappings are listed. Foundational/admin surfaces
 * (Overview, Connectors, Secrets, Team, Multi-Tenancy, Customers, Skill Center,
 * Intelligence, Flags, Templates) are intentionally absent = always visible.
 * Gating only takes effect when the customer's enforcement mode is ENFORCE and the
 * caller is not a Platform Admin (see useEntitlements / guard).
 *
 * Agent Marketplace gates under AGENT_GOV (M4): it leads to agent install/creation,
 * so it must hide whenever the Agentlets inventory (/agents, also AGENT_GOV) hides —
 * showing a marketplace whose Install would 403 was inconsistent.
 */

// Nav item id -> ModuleKey
export const NAV_ITEM_MODULE = {
  agents: ['AGENT_GOV', 'EXT_AGENT_GOV'], // /agents hosts both native + external archetypes
  'agent-marketplace': 'AGENT_GOV',       // install/create an agent → Agent Governance

  canvas: 'WORKFLOW_ORCH',
  'workflow-intelligence': 'WORKFLOW_ORCH',
  identity: 'IDENTITY_AUTHZ',
  'intent-profiles': 'IDENTITY_AUTHZ',
  approvals: 'GOVERNANCE',
  'protection-hub': 'GOVERNANCE',
  /* The old path still arrives from bookmarks and resolves to the
     same module while it redirects. */
  'governance-hub': 'GOVERNANCE',
  accountability: 'GOVERNANCE',
  'policy-config': 'GOVERNANCE',
  'workflow-audit': 'GOVERNANCE',
  compliance: 'TRUST_COMPLIANCE',
  'native-trust': 'AGENT_GOV',
  'shadow-discovery': 'SHADOW_DISCOVERY',
  'workflow-discovery': 'SHADOW_DISCOVERY',
  'gel-agents': 'EXT_AGENT_GOV',
  'gel-connectors': 'EXT_AGENT_GOV',
  'gateway-keys': 'API_GATEWAY',
  'gateway-dashboard': 'API_GATEWAY',
};

// Ordered [pathPrefix, ModuleKey] — first startsWith match wins. Most specific first.
const ROUTE_MODULE = [
  ['/workflow-intelligence', 'WORKFLOW_ORCH'],
  ['/workflow-discovery', 'SHADOW_DISCOVERY'],
  ['/canvas', 'WORKFLOW_ORCH'],
  ['/workflows', 'WORKFLOW_ORCH'],
  ['/discovery', 'SHADOW_DISCOVERY'],
  ['/agent-marketplace', 'AGENT_GOV'],   // install/create an agent → Agent Governance
  ['/agents', ['AGENT_GOV', 'EXT_AGENT_GOV']],
  ['/native-trust', 'AGENT_GOV'],
  ['/identity', 'IDENTITY_AUTHZ'],
  ['/intent-profiles', 'IDENTITY_AUTHZ'],
  ['/approvals', 'GOVERNANCE'],
  ['/protection-hub', 'GOVERNANCE'],
  /* The hub's former path. It redirects, but it is gated on the way
     through: a path missing from this list is treated as foundational and
     ungated, so dropping it would turn a rename into a bypass. */
  ['/governance-hub', 'GOVERNANCE'],
  ['/accountability', 'GOVERNANCE'],
  ['/policy-config', 'GOVERNANCE'],
  ['/policies', 'GOVERNANCE'],
  ['/workflow-audit', 'GOVERNANCE'],
  ['/compliance', 'TRUST_COMPLIANCE'],
  ['/gel-', 'EXT_AGENT_GOV'],
  ['/registrations', 'EXT_AGENT_GOV'],
  ['/gateway', 'API_GATEWAY'],
];

/** ModuleKey that gates a pathname, or null if the path is foundational/ungated. */
export function moduleForPath(pathname) {
  if (!pathname) return null;
  for (const [prefix, mod] of ROUTE_MODULE) {
    if (pathname.startsWith(prefix)) return mod;   // mod may be null (explicitly ungated)
  }
  return null;
}
